import React, { useEffect } from 'react'

import _ from 'lodash';
import cx from 'classnames';
import styles from './styles.module.scss';
import { useSelector } from 'react-redux';
import Actions from 'src/components/actionbar';
import IntegrationCard, { NotifyAction } from '../components/integrationCard';
import { hideSideMenu } from 'src/redux/actions/hub';
import { useDispatch } from 'react-redux';
import { ActionNeeded, RecentActivity, UserListCard, VersionUpdate } from '../components/smartCards';
import UserAvatar from '../components/userAvatar';
import Slider from 'src/components/slider';
import Button from 'src/components/button';
import { SearchBar } from 'src/components/input';
import TechStack from './techStack';
import withErrorBoundary from 'src/components/errorBoundary/withErrorBoundary';
import { useCapabilitiesFetch, useEventsFetch } from 'src/query/settings/dashboard';
import { useNotifyUser } from 'src/query/settings/users';
import { IntegrationCapability } from 'src/types/settings/dashboard';
import { useUsersFetch, useUserManagementStatusFetch } from 'src/query/settings/users';
import InfiniteScroll from 'src/components/infiniteScroll';
import { useCapabilitiesActionNeededFetch, useContentModeratorsFetch, useContentModeratorsUpdate } from 'src/query/settings/dashboard';
import { useNavigate } from 'react-router-dom';
import { useIntegrationsQuery } from 'src/query/settings/integrations';
import { calculatePercentage, getIntegrationPath } from 'src/utils/util';
import { useDashboardAdoption } from 'src/query/settings/adoption/dashboard';
import Toast from 'src/components/toast';
import { toast } from 'react-toastify';
import userRoleService from 'src/utils/userRoles.service';
import { SubscriptionPlan } from 'src/types/accounts';
import ScoreMeter from 'src/components/scoreMeter';
import subscriptionPlanService from 'src/utils/subscriptionPlan.service';

const SelectedIntegrationsEmptyImage = '/images/screwDriver.svg'
const dashboardEmptyMessage = '/images/dashboardEmptyMessage.svg'

function Dashboard() {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state: any) => _.get(state, 'users.me', {}));
  const userAvatar = _.get(user, 'icon.full', '');

  const { trackManageTechStackClicked, trackContentModeratorsAdded, trackNotifyUsersClicked } = useDashboardAdoption();
  const { data: capabilities, isLoading: isLoadingCapabilities } = useCapabilitiesFetch();
  const { mutate: notifyUserMutate, isPending: isNotifyUserPending, isSuccess: isNotifyUserSuccess, isError: isNotifyUserError } = useNotifyUser();
  const { data: integrations, isLoading: isLoadingIntegrations } = useIntegrationsQuery();

  const { data : events, hasNextPage : hasNextPageEvents, loadMore : loadMoreEvents, isFetching : isFetchingEvents, hasRecords : hasEvents } = useEventsFetch();
  const [techStackView, setTechStackView] = React.useState(false);
  const [notifyUser, setNotifyUser] = React.useState(false);
  const [showAddUsers, setShowAddUsers] = React.useState(false);
  const [selectedCapability, setSelectedCapability] = React.useState<IntegrationCapability | null>(null);
  const [showAddContentModerators, setShowAddContentModerators] = React.useState(false);
  const [searchUsers, setSearchUsers] = React.useState<string>("");
  const [averagePercentage, setAveragePercentage] = React.useState(0);
  const [scoreMeterAnimatedValue, setScoreMeterAnimatedValue] = React.useState(0);

  const { hasNextPage, data: users, loadMore: loadMoreUsers, refetch: refetchUsers, isFetching, hasRecords: hasUsers } = useUsersFetch(searchUsers);
  const { data: usersStatus } = useUserManagementStatusFetch();
  const { data: activeContentModerators } = useContentModeratorsFetch();
  const { mutate: updateContentModerators, isPending, isSuccess, isError } = useContentModeratorsUpdate();
  const { data: capabilitiesActionNeeded } = useCapabilitiesActionNeededFetch();

  const actionNeeded = _.filter(capabilitiesActionNeeded, (capability) => capability?.productCapabilityResponse?.category?.type === "ACCOUNT_SPECIFIC");
  const [selectedContentModerators, setSelectedContentModerators] = React.useState<string[]>([]);
  const activatedIntegrationsKey = _.map(integrations, (integration) => integration.key);
  const enabledIntegrations = _.filter(capabilities, (capability) => capability?.enabled);
  const usersRecords = _.flatten(_.map(users?.pages, (pages) => pages.records));

  const notActivatedAndEnabledIntegrations = _.filter(enabledIntegrations, (capability) => {
    const type = capability?.productCapabilityResponse?.type;
    return capability?.productCapabilityResponse?.category?.type === "ACCOUNT_SPECIFIC" && !activatedIntegrationsKey?.includes(type);
  });

  useEffect(() => {
    refetchUsers();
  }, [searchUsers]);

  useEffect(() => {
    if(capabilities){
     const enabledCapabilities = _.filter(capabilities, (capability) => capability.enabled);
     if(enabledCapabilities){
      const averagePercentage =  _.meanBy(
           _.filter(enabledCapabilities, (item) => _.isNumber(item.percentage)), 
      "percentage");
      setAveragePercentage(averagePercentage);
     }
    }
  }, [capabilities]);

  useEffect(() => {
      setScoreMeterAnimatedValue(0);
  
      const animationDuration = 2000;
      const steps = 60;
      const stepDuration = animationDuration / steps;
      const increment = averagePercentage / steps;
  
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const newValue = Math.min(increment * step, averagePercentage);
        setScoreMeterAnimatedValue(newValue);
  
        if (step >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
  
      return () => clearInterval(timer);
  }, [averagePercentage]);

  useEffect(() => {
    if (activeContentModerators) {
      setSelectedContentModerators(activeContentModerators);
    }
  }, [activeContentModerators]);

  useEffect(() => {
    if (isSuccess || isError) {
      if (isSuccess) {
        if(_.size(selectedContentModerators) > _.size(activeContentModerators)){
          trackContentModeratorsAdded();
        }
        toast(<Toast.Sucess message={"Successfully updated content moderator"} />, {
          autoClose: 3000,
        });
      }
      setShowAddContentModerators(false);
    }
  }, [isSuccess, isError]);

  useEffect(() => {
    dispatch(hideSideMenu(techStackView))
  }, [techStackView]);

  useEffect(() => {
    if (isNotifyUserSuccess || isNotifyUserError) {
      if (isNotifyUserSuccess) {
        trackNotifyUsersClicked();
        toast(<Toast.Sucess message={`Successfully notified users`} />, {
          autoClose: 3000,
        });
      }
      setNotifyUser(false);
    }
  }, [isNotifyUserSuccess, isNotifyUserError]);

  const handleAddUsers = () => {
    setShowAddContentModerators(true);
  }

  const handleUpdateContentModerators = () => {
    const body = _.map(selectedContentModerators, (id) => {
      return {
        userId: id,
      }
    });
    updateContentModerators(body);
  }

  const handleActionNeededClick = (integration) => {
    const integrationType = integration.productCapabilityResponse?.type;
    if (integrationType) {
      const getPath = getIntegrationPath(integrationType);
      if (getPath) {
        navigate(`/settings/integrations/${getPath.toLowerCase()}`);
      }
    }
  }

  const handleCancelAddContentModerators = () => {
    setShowAddContentModerators(false);
    setSelectedContentModerators(activeContentModerators || []);
  }

  if (!user.admin) {
    return <div className={styles.loadingMessage}>ACCESS RESTRICTED</div>
  }

  if (techStackView) {
    return (
      <TechStack setTechStackView={setTechStackView} />
    )
  }

  if (isLoadingCapabilities) {
    return <div className={styles.loadingMessage}>Loading...</div>;
  }

  const isStarterPlan = subscriptionPlanService.isStarterPlan();

  if (isStarterPlan) {
    return <div className={cx(styles.startedEmptyMessage)}>
      <div className={styles.verticalCenter}>
        <img src={dashboardEmptyMessage} alt="No Integrations" />
        <p className={styles.title}>Dashboard requires plan upgrade</p>
        <p className={styles.subTitle}>Contact our team to upgrade and unlock dashboard features.</p>
        <button id="intercom_launcher" className={styles.actionButton}>  <i className={cx(styles.icon, "paperflite-text-message")} /> Get in touch </button>
      </div>
    </div>

  }

  const handleNotifyUser = (id: string) => {
    setNotifyUser(true);
    setSelectedCapability(capabilities.find(capability => capability.id === id) || null);
  };

  const contentModeratorsUsers = _.filter(usersRecords, (user) => {
    return activeContentModerators?.includes(user.id);
  });

  const enabledCapabilities = _.filter(capabilities,
    (capability) => capability.enabled
  );


  return (
    <div className={cx(styles.dashboard)}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <UserAvatar width={"2.5rem"} height={"2.5rem"} src={userAvatar} />
          <div>
            <p className={styles.title}>Welcome,</p>
            <h3 className={styles.subText}>{_.get(user, "firstName")} {_.get(user, "lastName")}</h3>
          </div>
        </div>
        {!isStarterPlan && <Actions.Button
          width={4}
          icon={"paperflite-add"}
          title="manage tech stack"
          onClick={() => {
            trackManageTechStackClicked();
            setTechStackView(!techStackView);
          }}
        />}
      </div>

      <div className={styles.contentContainer}>
      
        <div className={styles.leftPane}>
               {_.size(enabledCapabilities) > 0 ?
         <>
          <div className={styles.scoreContainer}>
            <div className={styles.scoreMeter}>
             <ScoreMeter animatedValue={scoreMeterAnimatedValue} />
            </div>
            <p className={cx(styles.title)} >
              Here’s your integration health score, determined <br/> by the following integrated sources.
            </p>
          </div>

        <div className={styles.techStack}>
          { enabledCapabilities.map((capability) => {
              return <IntegrationCard
                key={capability.id}
                title={capability.productCapabilityResponse.name}
                subTitle={capability.percentage === 100 ? "Integration done by all team members" : `${capability.totalUsers - capability.count} of ${capability.totalUsers} team members need to set up`}
                className={styles.integrationCard}
                type="notify"
                actionType={
                  capability.percentage === 100 ? <AllDone /> :
                    <NotifyAction
                      percentage={capability.percentage}
                      showNotifyButton={capability.percentage !== 100}
                      handleNotifyUser={() => handleNotifyUser(capability.id)}
                    />
                }
                iconUrl={capability.productCapabilityResponse.icon}
              />
            })
          }
        </div> </> :
         <div className={cx(styles.emptyMessage)}>
            <div className={styles.verticalCenter}>
              <img src={dashboardEmptyMessage} alt="No Integrations" />
              <p className={styles.title}>Set up your tech stack to get started</p>
              <p className={styles.subTitle}> Configure your integrations to view your dashboard. </p>
              <button onClick={() => {
                setTechStackView(!techStackView);
              }} className={styles.actionButton}>  <i className={cx(styles.icon, "paperflite-integrations")} /> manage tech stack </button>
            </div>
          </div> }
          </div> 

        <div className={styles.rightPane}>
          {(_.size(actionNeeded) > 0 || _.size(notActivatedAndEnabledIntegrations) > 0) && <div className={styles.actionNeeded}>
            <p className={styles.title}>action needed</p>

            <div className={styles.innerContainer}>

              {_.size(actionNeeded) > 0 && actionNeeded.map((item: any, index: number) => (
                <ActionNeeded key={index} isIntegrated={true} name={item?.productCapabilityResponse?.name} icon={item?.productCapabilityResponse?.icon}
                  handleClick={() => handleActionNeededClick(item)}
                />
              ))}

              {notActivatedAndEnabledIntegrations?.map((item: any, index: number) => (
                <ActionNeeded key={index} isIntegrated={false} name={item?.productCapabilityResponse?.name} icon={item?.productCapabilityResponse?.icon}
                  handleClick={() => handleActionNeededClick(item)}
                />))}

            </div>
          </div>}

        {_.size(_.get(events,"pages[0].records")) > 0 && <div className={styles.recentActivities}>
            <p className={styles.title}>recent activities</p>
            <InfiniteScroll
              hasMore={hasNextPageEvents}
              loading={isFetchingEvents}
              loadMore={loadMoreEvents}
              loader={() => (
                <div className={styles.justifyCenter}>loading...</div>
              )}
              className={styles.innerContainer}
            >

              {events?.pages?.map((page) => (
                <>
                  {page?.records?.map((event, index) => (
                    <RecentActivity key={index} event={event} />
                  ))}
                </>
              ))}

            </InfiniteScroll>

          </div>}

          <div className={styles.userManagement}>
            <p className={styles.userCount}>
              <span className={styles.subText}> <i className={cx(styles.dot, styles.yellow)} /> pending invitations </span>
              <span className={styles.count}>{usersStatus?.pendingInvitationCount}</span>
            </p>
          </div>

          {!isStarterPlan && <div className={styles.contentModerators}>
            <p className={styles.title} >content moderators</p>

            <div className={styles.contentModeratorsUsers}>
              {_.size(contentModeratorsUsers) > 0 ?
                (_.map(contentModeratorsUsers, (user, index) => (
                  <ContentRow key={index} name={`${user.firstName} ${user.lastName && user.lastName}`} email={user.email} icon={_.get(user, 'icon.full', '')}/>
                ))) : <p className={styles.subText}>Set up content moderators to receive alerts when searches return no results.</p>
              }
            </div>

            <p className={styles.actionItem} onClick={handleAddUsers}> <i className='paperflite-add' /> add content moderators </p>
          </div>}
        </div>

      </div>

      <NotifyActionSlider
        show={notifyUser}
        onClose={() => setNotifyUser(false)}
        isNotifyUserPending={isNotifyUserPending}
        handleNotifyUserClick={(type) => notifyUserMutate(type)}
        capability={selectedCapability}
      />

      <Slider.Dialog
        width="300"
        show={showAddContentModerators}
        onClose={handleCancelAddContentModerators}
      >
        <Slider.Header
          title="add content moderators"
          message="moderators receive alerts when a search returns no results and the user chooses to notify." />
        <Slider.Body>
          <div className={styles.addUserSlider}>
            <div className={styles.searchContainer}>
              <SearchBar query={searchUsers} onSearch={(query) => setSearchUsers(query ? query : "")} />
            </div>

            <div>
              <InfiniteScroll
                hasMore={hasNextPage}
                loading={isFetching}
                loadMore={loadMoreUsers}
                loader={() => (
                  <div className={styles.justifyCenter}>loading...</div>
                )}
                className={styles.addUserList}
              >
                {!isFetching && !hasUsers && (
                  <div className={styles.justifyCenter}>No users found</div>
                )}

                {users?.pages?.map((page) => (
                  <>
                    {page?.records?.map((user, index) => user.admin && (
                      <UserListCard name={user.firstName} icon={_.get(user, 'icon.full')}
                        email={user.email} role={user.role} key={index}
                        isSelected={selectedContentModerators?.includes(user.id)}
                        id={user.id}
                        handleSelect={(id) => {
                          if (selectedContentModerators?.includes(id)) {
                            setSelectedContentModerators(selectedContentModerators?.filter((item) => item !== id));
                          } else {
                            setSelectedContentModerators([...selectedContentModerators, id]);
                          }
                        }}
                      />
                    ))}
                  </>
                ))}
              </InfiniteScroll>

            </div>
          </div>

        </Slider.Body>

        <Slider.Actions>
          <Button variant="primary" icon='paperflite-tick' text={isPending ? "Updating..." : "Confirm"} disabled={isPending} onClick={handleUpdateContentModerators}></Button>
          <Button variant="secondary" text="Cancel" disabled={false} onClick={handleCancelAddContentModerators}></Button>
        </Slider.Actions>
      </Slider.Dialog>

    </div>
  )
}

export const NotifyActionSlider = ({
  show,
  onClose,
  isNotifyUserPending,
  handleNotifyUserClick,
  capability
}: {
  show: boolean;
  onClose: () => void;
  isNotifyUserPending: boolean;
  handleNotifyUserClick: (id: string) => void;
  capability: IntegrationCapability | null;
}) => {
  return (
    <Slider.Dialog
      width="300"
      show={show}
      onClose={() => onClose()}
    >
      <Slider.Header
        title="notify users"
        message={capability?.productCapabilityResponse.name} />
      <Slider.Body>
        <p className={styles.description}>
          This will prompt users to finish {capability?.productCapabilityResponse.name} setup and start using Paperflite.
        </p>
      </Slider.Body>

      <Slider.Actions>
        <Button variant="primary" icon='paperflite-tick' text={isNotifyUserPending ? "Notifying..." : "Confirm"} disabled={isNotifyUserPending} onClick={() => handleNotifyUserClick(capability?.productCapabilityResponse.type || "")}></Button>
        <Button variant="secondary" text="Cancel" disabled={isNotifyUserPending} onClick={() => onClose()}></Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}


export const ContentRow = (
  { name,
    email,
    icon
  }: {
    name: string;
    email: string;
    icon?:string;
  }) => {
  return (
    <div className={styles.contentRow}>
      <UserAvatar width={"2.5rem"} height={"2.5rem"} src={icon} />
      <div className={styles.contentInfo}>
        <p className={styles.name}>{name}</p>
        <p className={styles.email}>{email}</p>
      </div>
    </div>
  )
}


export const AllDone = () => {
  return <div className={styles.allDone}>
    <p><i className='paperflite-tick' /> All done</p>
  </div>
}

export default withErrorBoundary(Dashboard); 